import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CaretDown, ChatCircleDots, PaperPlaneTilt, PushPin } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { renderMentions } from "@/components/TeamNotes";

const when = (iso) =>
  iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "";

export const MobileTeamNotes = ({ projectId, testid }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!projectId) return undefined;
    let alive = true;
    api.get(`/projects/${projectId}/team-notes`).then(({ data }) => alive && setData(data)).catch(() => {});
    return () => {
      alive = false;
    };
  }, [projectId]);

  if (!projectId || !data) return null;
  const notes = data.notes || [];
  const pinned = notes.find((n) => n.id === data.pinned_note_id);

  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data: p } = await api.post(`/projects/${projectId}/team-notes`, { text });
      setData({ notes: (p.team_notes || []).slice(-20), pinned_note_id: p.pinned_note_id || "", code: p.code });
      setText("");
      toast.success("Note posted");
    } catch {
      toast.error("Could not post note");
    }
    setBusy(false);
  };

  return (
    <div className="mt-2" data-testid={`${testid}-team-notes`}>
      {pinned && (
        <div
          data-testid={`${testid}-pinned-note`}
          className="flex items-start gap-1.5 rounded-lg border border-amber-400/40 bg-amber-500/10 px-2.5 py-1.5 text-xs text-amber-50"
        >
          <PushPin size={12} weight="fill" className="mt-0.5 shrink-0 text-amber-300" />
          <span className="min-w-0 whitespace-pre-wrap break-words">{renderMentions(pinned.text)}</span>
        </div>
      )}
      <button
        type="button"
        data-testid={`${testid}-team-notes-toggle`}
        onClick={() => setOpen((o) => !o)}
        className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-widest text-zinc-400"
      >
        <ChatCircleDots size={13} weight="bold" /> Team notes ({notes.length})
        <CaretDown size={11} weight="bold" className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="fixed inset-0 z-[200] flex items-end bg-black/70 backdrop-blur-sm" data-testid={`${testid}-team-notes-sheet`} onClick={() => setOpen(false)}>
          <div
            className="max-h-[85vh] w-full overflow-y-auto rounded-t-3xl border-t border-zinc-800 bg-zinc-950 px-4 pb-24 pt-3 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-zinc-700" />
            <div className="flex items-center justify-between">
              <div className="text-xs font-bold uppercase tracking-widest text-indigo-300">Team notes ({notes.length})</div>
              <button type="button" data-testid={`${testid}-team-notes-close`} onClick={() => setOpen(false)} className="text-xs uppercase tracking-widest text-zinc-500">Close</button>
            </div>
            <div className="mt-3 space-y-2">
          {notes.length === 0 && <p className="text-xs text-zinc-500">No team notes yet.</p>}
          {notes.slice(-20).map((n) => (
            <div
              key={n.id}
              data-testid={`${testid}-note-${n.id}`}
              className="rounded-xl rounded-bl-sm border border-zinc-800 bg-zinc-950 px-3 py-2 text-xs text-zinc-100"
            >
              <div className="whitespace-pre-wrap break-words">{renderMentions(n.text)}</div>
              <div className="mt-0.5 text-[10px] uppercase tracking-widest text-zinc-500">
                {n.author} · {when(n.at)}
              </div>
            </div>
          ))}
          <div className="flex gap-2">
            <input
              data-testid={`${testid}-note-input`}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Quick note for the office…"
              className="h-9 min-w-0 flex-1 rounded-lg border border-zinc-800 bg-zinc-950 px-3 text-sm text-zinc-100 outline-none focus:border-indigo-400"
            />
            <button
              type="button"
              data-testid={`${testid}-note-send`}
              disabled={busy || !text.trim()}
              onClick={send}
              className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-500 text-white disabled:opacity-40"
            >
              <PaperPlaneTilt size={15} weight="bold" />
            </button>
          </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
